import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useEntitlements } from '../context/EntitlementsContext';
import { CaretLeft, SignOut, SealCheck, Question, UserCircle } from '@phosphor-icons/react';
import HelpDrawer from './HelpDrawer';
import OfflineIndicator from './OfflineIndicator';

const TIER_STYLES = {
    foundation: { label: 'Tier: Foundation', bg: '#F4F4F5', fg: '#52525B', border: '#D4D4D8' },
    rectify:    { label: 'Tier: Rectify',    bg: '#E8F5FB', fg: '#1787AB', border: '#1FA5CF' },
    surveyor:   { label: 'Tier: Surveyor',   bg: '#18181B', fg: '#FFFFFF', border: '#18181B' },
    architect:  { label: 'Tier: Architect',  bg: '#E8F5FB', fg: '#1787AB', border: '#1FA5CF' },
};

function TierBadge({ tier }) {
    const style = TIER_STYLES[tier] || TIER_STYLES.foundation;
    const isArchitect = tier === 'architect';
    return (
        <span
            data-testid={`tier-badge-${tier}`}
            title={isArchitect ? 'Architect — invite-only lifetime access' : `${style.label} tier`}
            className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] font-mono font-semibold uppercase tracking-[0.14em] border"
            style={{ background: style.bg, color: style.fg, borderColor: style.border }}
        >
            {isArchitect && <SealCheck size={10} weight="fill" />}
            {style.label}
        </span>
    );
}

export default function AppHeader({ title, back = false, right = null }) {
    const navigate = useNavigate();
    const { user, logout } = useAuth();
    const ent = useEntitlements();
    const [helpOpen, setHelpOpen] = useState(false);
    return (
        <header
            className="sticky top-0 z-30 bg-white border-b border-[#18181B] no-print"
            style={{ paddingTop: 'env(safe-area-inset-top)' }}
            data-testid="app-header"
        >
            <div className="flex items-center gap-3 px-4 py-3">
                {back ? (
                    <button
                        onClick={() => navigate(-1)}
                        data-testid="header-back"
                        className="p-2 -ml-2 hover:bg-[#F4F4F5]"
                    >
                        <CaretLeft size={22} weight="bold" />
                    </button>
                ) : (
                    <div className="flex items-center -my-1">
                        <img
                            src="/defectr-logo.png"
                            alt="DefectR"
                            className="h-9 w-auto object-contain"
                        />
                    </div>
                )}
                <div className="flex-1 min-w-0">
                    {/* Title/subtitle hidden — bottom nav already indicates
                        the active page, and on narrow mobile viewports the
                        title was colliding with the tier badge. */}
                    {title && <h1 className="sr-only">{title}</h1>}
                </div>
                {right}
                {user && <OfflineIndicator />}
                {ent?.ready && user && <TierBadge tier={ent.tier} />}
                {user && (
                    <button
                        onClick={() => setHelpOpen(true)}
                        data-testid="help-btn"
                        className="p-2 hover:bg-[#E8F5FB] hover:text-[#1787AB] transition-colors duration-150"
                        title="Help & how-to"
                        aria-label="Open help"
                    >
                        <Question size={20} weight="bold" />
                    </button>
                )}
                {!back && user && (
                    <button
                        onClick={() => navigate('/app/account')}
                        data-testid="account-btn"
                        className="p-2 hover:bg-[#F4F4F5]"
                        title="Account & privacy"
                        aria-label="Account & privacy"
                    >
                        <UserCircle size={20} weight="bold" />
                    </button>
                )}
                {!back && user && (
                    <button
                        onClick={async () => { await logout(); navigate('/'); }}
                        data-testid="logout-btn"
                        className="p-2 hover:bg-[#F4F4F5]"
                        title="Sign out"
                    >
                        <SignOut size={20} />
                    </button>
                )}
            </div>
            <HelpDrawer open={helpOpen} onClose={() => setHelpOpen(false)} />
        </header>
    );
}
